<template>
  <!-- 서비스 페이지 섹션 편집 드로어 -->
  <a-drawer
    :visible="visible"
    placement="right"
    :width="drawerWidth"
    :title="sectionTitle"
    :mask-closable="false"
    @close="handleCancel"
  >
    <!-- Hero 섹션 편집 -->
    <template v-if="section === 'hero'">
      <div class="field">
        <label class="field__label">제목 <span class="required">*</span></label>
        <a-input
          v-model:value="form.hero.title"
          :maxlength="100"
          show-count
          placeholder="히어로 제목 (최대 100자)"
        />
      </div>
      <div class="field">
        <label class="field__label">부제목</label>
        <a-textarea
          v-model:value="form.hero.subtitle"
          :maxlength="100"
          show-count
          :rows="3"
          placeholder="히어로 부제목 (최대 100자)"
        />
      </div>
      <div class="field">
        <label class="field__label">히어로 이미지</label>
        <image-uploader
          :model-value="form.hero.heroImage"
          @update:model-value="(url) => (form.hero.heroImage = url)"
        />
      </div>
      <div class="field">
        <label class="field__label">CTA 버튼 텍스트</label>
        <a-input
          v-model:value="form.hero.ctaText"
          placeholder="예: 지금 시작하기"
        />
      </div>
      <div class="field">
        <label class="field__label">CTA 버튼 URL</label>
        <a-input
          v-model:value="form.hero.ctaUrl"
          type="url"
          placeholder="https://..."
        />
      </div>
    </template>

    <!-- Features 섹션 편집 -->
    <template v-else-if="section === 'features'">
      <div class="features-header">
        <span class="features-count">{{ form.features.length }}개 기능</span>
        <a-button
          type="primary"
          class="yellow-button"
          size="small"
          @click="addFeature"
        >
          + 기능 추가
        </a-button>
      </div>

      <div
        v-for="(feat, idx) in form.features"
        :key="idx"
        class="feature-item"
      >
        <div class="feature-item__header">
          <span class="feature-item__num">기능 {{ idx + 1 }}</span>
          <a-button
            type="text"
            danger
            size="small"
            @click="removeFeature(idx)"
          >
            삭제
          </a-button>
        </div>
        <div class="field">
          <label class="field__label">제목</label>
          <a-input
            v-model:value="form.features[idx].title"
            placeholder="기능 제목"
          />
        </div>
        <div class="field">
          <label class="field__label">설명</label>
          <a-textarea
            v-model:value="form.features[idx].description"
            :rows="2"
            placeholder="기능 설명"
          />
        </div>
        <div class="field">
          <label class="field__label">아이콘 이미지</label>
          <image-uploader
            :model-value="form.features[idx].iconUrl"
            :max-size-m-b="2"
            @update:model-value="(url) => (form.features[idx].iconUrl = url)"
          />
        </div>
        <a-divider style="margin: 16px 0;" />
      </div>

      <a-empty
        v-if="form.features.length === 0"
        description="등록된 기능이 없습니다."
        style="margin: 24px 0;"
      />
    </template>

    <!-- Reviews 섹션 편집 -->
    <template v-else-if="section === 'reviews'">
      <div class="features-header">
        <span class="features-count">{{ form.reviews.length }}개 후기</span>
        <a-button
          type="primary"
          class="yellow-button"
          size="small"
          @click="addReview"
        >
          + 후기 추가
        </a-button>
      </div>

      <div
        v-for="(review, idx) in form.reviews"
        :key="idx"
        class="feature-item"
      >
        <div class="feature-item__header">
          <span class="feature-item__num">후기 {{ idx + 1 }}</span>
          <a-button
            type="text"
            danger
            size="small"
            @click="removeReview(idx)"
          >
            삭제
          </a-button>
        </div>
        <div class="field">
          <label class="field__label">작성자</label>
          <a-input
            v-model:value="form.reviews[idx].author"
            placeholder="작성자 이름"
          />
        </div>
        <div class="field">
          <label class="field__label">후기 내용</label>
          <a-textarea
            v-model:value="form.reviews[idx].content"
            :rows="3"
            placeholder="후기 내용"
          />
        </div>
        <div class="field">
          <label class="field__label">별점</label>
          <a-rate v-model:value="form.reviews[idx].rating" />
        </div>
        <div class="field">
          <label class="field__label">프로필 이미지</label>
          <image-uploader
            :model-value="form.reviews[idx].imageUrl"
            :max-size-m-b="2"
            @update:model-value="(url) => (form.reviews[idx].imageUrl = url)"
          />
        </div>
        <a-divider style="margin: 16px 0;" />
      </div>

      <a-empty
        v-if="form.reviews.length === 0"
        description="등록된 후기가 없습니다."
        style="margin: 24px 0;"
      />
    </template>

    <!-- 드로어 하단 액션 버튼 -->
    <template #extra>
      <a-space>
        <a-button @click="handleCancel" :disabled="loading">취소</a-button>
        <a-button
          type="primary"
          class="yellow-button"
          :loading="loading"
          @click="handleSave"
        >
          저장
        </a-button>
      </a-space>
    </template>
  </a-drawer>
</template>

<script>
import { message } from 'ant-design-vue';
import ImageUploader from '../common/ImageUploader.vue';
import { HomepageApi } from '../../api';

// 섹션별 제목 매핑
const SECTION_TITLES = {
  hero: '히어로 배너 편집',
  features: '주요 기능 편집',
  reviews: '사용자 후기 편집',
};

export default {
  name: 'ServiceSectionEditor',

  components: {
    ImageUploader,
  },

  props: {
    // 드로어 표시 여부
    visible: {
      type: Boolean,
      default: false,
    },
    // 편집할 섹션: 'hero' | 'features' | 'reviews'
    section: {
      type: String,
      default: 'hero',
    },
    // 서비스 slug
    slug: {
      type: String,
      default: '',
    },
    // 서비스 전체 데이터 객체
    data: {
      type: Object,
      default: null,
    },
  },

  emits: ['cancel'],

  data() {
    return {
      loading: false,
      // 로컬 편집 폼
      form: {
        hero: {
          title: '',
          subtitle: '',
          heroImage: '',
          ctaText: '',
          ctaUrl: '',
        },
        features: [],
        reviews: [],
      },
    };
  },

  computed: {
    // 반응형 드로어 너비 (1440px 이상은 520, 미만은 420)
    drawerWidth() {
      return window.innerWidth >= 1440 ? 520 : 420;
    },

    // 현재 섹션 제목
    sectionTitle() {
      return SECTION_TITLES[this.section] || '섹션 편집';
    },
  },

  watch: {
    // 드로어 열릴 때 폼 데이터 초기화
    visible(val) {
      if (val) {
        this.initForm();
      }
    },
  },

  methods: {
    // 폼 데이터 초기화 (기존 서비스 데이터 기반)
    initForm() {
      const src = this.data || {};

      this.form.hero = {
        title: src.hero?.title || '',
        subtitle: src.hero?.subtitle || '',
        heroImage: src.hero?.heroImage || '',
        ctaText: src.hero?.ctaText || '',
        ctaUrl: src.hero?.ctaUrl || '',
      };

      this.form.features = Array.isArray(src.features)
        ? src.features.map((f) => ({
            title: f.title || '',
            description: f.description || '',
            iconUrl: f.iconUrl || '',
          }))
        : [];

      this.form.reviews = Array.isArray(src.reviews)
        ? src.reviews.map((r) => ({
            author: r.author || '',
            content: r.content || '',
            rating: r.rating || 5,
            imageUrl: r.imageUrl || '',
          }))
        : [];
    },

    // 기능 항목 추가
    addFeature() {
      this.form.features.push({ title: '', description: '', iconUrl: '' });
    },

    // 기능 항목 제거
    removeFeature(idx) {
      this.form.features.splice(idx, 1);
    },

    // 후기 항목 추가
    addReview() {
      this.form.reviews.push({ author: '', content: '', rating: 5, imageUrl: '' });
    },

    // 후기 항목 제거
    removeReview(idx) {
      this.form.reviews.splice(idx, 1);
    },

    // 유효성 검사
    validate() {
      if (this.section === 'hero') {
        if (!this.form.hero.title || !this.form.hero.title.trim()) {
          message.warning('히어로 제목을 입력해주세요.');
          return false;
        }
      }
      return true;
    },

    // 저장: 기존 데이터에 편집된 섹션 병합 후 upsert
    async handleSave() {
      if (!this.validate()) return;

      this.loading = true;
      try {
        // 기존 데이터에 변경된 섹션만 덮어쓰기
        const updatedData = {
          ...(this.data || {}),
          slug: this.slug,
          hero: this.form.hero,
          features: this.form.features,
          reviews: this.form.reviews,
        };

        await HomepageApi.upsertServicePage(updatedData);
        message.success('서비스 페이지가 저장되었습니다.');
        this.$emit('cancel');
      } catch (error) {
        message.error(error.message || '저장 중 오류가 발생했습니다.');
      } finally {
        this.loading = false;
      }
    },

    // 취소/닫기
    handleCancel() {
      if (!this.loading) {
        this.$emit('cancel');
      }
    },
  },
};
</script>

<style scoped>
.field {
  margin-bottom: 16px;
}

.field__label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #333;
}

.required {
  color: #ff4d4f;
}

.features-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.features-count {
  font-size: 13px;
  color: #888;
}

.feature-item {
  background: #fafafa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}

.feature-item__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.feature-item__num {
  font-size: 13px;
  font-weight: 600;
  color: #555;
}
</style>
